import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isChecked, isIndeterminate, rootDomain, UrlTreeNode } from './selection';
import { QueryKey } from '@/Config/api/queryKey';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Favicon } from '@/Domain/components/Shared/Favicon/Favicon';
import { cn } from '@/Domain/utils/cn';

const CHILDREN_PAGE_SIZE = 50;

interface UrlTreeProps {
  node: UrlTreeNode;
  selection: Set<string>;
  onToggle: (path: string) => void;
  // Grafts lazily-loaded children into the shared tree (the modal writes them
  // into the preview query cache) so selection counting sees every loaded node.
  onChildrenLoaded: (path: string, batch: UrlTreeNode[]) => void;
  depth?: number;
}

export const UrlTree = ({
  node,
  selection,
  onToggle,
  onChildrenLoaded,
  depth = 0,
}: UrlTreeProps) => {
  const { t, i18n } = useTranslation(['knowledge_base']);
  const queryClient = useQueryClient();
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const sentinelRef = useRef<HTMLButtonElement>(null);

  const children = node.children ?? [];
  const hasChildren = children.length > 0 || node.truncated > 0;
  // Top two levels open by default; deeper levels collapsed to keep it scannable.
  const [expanded, setExpanded] = useState(depth < 2);

  const checked = isChecked(node.path, selection);
  const indeterminate = isIndeterminate(node.path, selection);

  const remaining = node.truncated;
  const hasMore = remaining > 0 && !exhausted;
  // The root may be a synthetic multi-host node with an empty path/label; derive
  // a domain so it shows a favicon + name instead of a nameless placeholder.
  const rootLabel = depth === 0 ? rootDomain(node) : '';

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await queryClient.fetchQuery<{ children: UrlTreeNode[] }>({
        queryKey: [
          QueryKey.knowledgeBase.siteMapPreview.children,
          { path: node.path, offset: children.length, limit: CHILDREN_PAGE_SIZE },
        ],
      });
      const batch = result.children ?? [];
      if (batch.length === 0) setExhausted(true);
      else onChildrenLoaded(node.path, batch);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, queryClient, node.path, children.length, onChildrenLoaded]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !expanded || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, hasMore, loadMore]);

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 rounded-md hover:bg-muted/60"
        style={{ paddingLeft: depth * 16 }}
        data-testid={`url-tree-node-${node.path}`}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            aria-label={t('tabs.website.review.toggle_group')}
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}

        <Checkbox
          checked={indeterminate ? 'indeterminate' : checked}
          onCheckedChange={() => onToggle(node.path)}
          data-testid={`url-tree-checkbox-${node.path}`}
        />

        {depth === 0 && <Favicon url={rootLabel} className="h-4 w-4 shrink-0 rounded-sm" />}

        <span
          className="min-w-0 flex-1 truncate text-sm text-foreground"
          title={rootLabel || node.path}
        >
          {depth === 0 ? (
            rootLabel || node.label || node.path
          ) : (
            <>
              <span className="text-foreground-subtle">/</span>
              {node.label}
            </>
          )}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {node.count.toLocaleString(i18n.language)}
        </span>
      </div>

      {hasChildren && expanded && (
        <div>
          {children.map((child) => (
            <UrlTree
              key={child.path}
              node={child}
              selection={selection}
              onToggle={onToggle}
              onChildrenLoaded={onChildrenLoaded}
              depth={depth + 1}
            />
          ))}
          {hasMore && (
            <button
              ref={sentinelRef}
              type="button"
              onClick={() => void loadMore()}
              className="flex w-full items-center gap-1.5 py-1 text-left text-xs text-foreground-subtle hover:text-muted-foreground"
              style={{ paddingLeft: (depth + 1) * 16 + 24 }}
              data-testid={`url-tree-more-${node.path}`}
            >
              {loadingMore && <Loader2 className="size-3 animate-spin" />}
              {t('tabs.website.review.more_groups', {
                count: remaining.toLocaleString(i18n.language),
              })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
